// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: f58b72db-49fa-497e-869c-5d6393ffaa0c

// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

import React, { useState } from 'react';
import { Box, IconButton, Link, Popover, Typography } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { getHelpEntry } from './helpContent';

const GREY = '#9AA6B2';

// Small "?" bubble beside a field label or a screen title — the LeanIX-style
// help pattern. Looks itself up in helpContent.js by `topic` and renders
// NOTHING when that topic has no entry, so a missing entry is silently no
// bubble rather than a broken/empty one.
//
// "Open in wiki" does not take a navigation prop — HelpBubble has no idea how
// the host app routes. It sets `#help=<topic>` and fires a
// `primordia:open-help` CustomEvent; CmdbApp listens for that event once and
// owns the actual navigation. This keeps HelpBubble usable from any screen
// without threading a callback through every caller.
export function openHelpTopic(topic) {
  if (!topic) return;
  try { window.location.hash = `help=${encodeURIComponent(topic)}`; } catch (e) { /* non-browser env */ }
  window.dispatchEvent(new CustomEvent('primordia:open-help', { detail: { topic } }));
}

export default function HelpBubble({ topic, size = 'small' }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const entry = getHelpEntry(topic);

  // Nothing to show — render nothing. A caller should never see an empty
  // bubble just because content has not been written yet for this topic.
  if (!entry) return null;

  const open = Boolean(anchorEl);
  const iconSize = size === 'small' ? 'small' : 'medium';

  const goToWiki = () => {
    setAnchorEl(null);
    openHelpTopic(entry.key);
  };

  return (
    <>
      <IconButton
        size={size}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-label={`Help: ${entry.title}`}
        sx={{ color: GREY, p: size === 'small' ? 0.25 : 0.5, '&:hover': { color: '#5C6B7A' } }}
      >
        <HelpOutlineIcon fontSize={iconSize} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, maxWidth: 360 } }}
      >
        <Typography sx={{ fontWeight: 700, color: 'sectionHeader.main', mb: 0.5, fontSize: 14 }}>{entry.title}</Typography>
        <Typography sx={{ fontSize: 13, color: '#333', lineHeight: 1.5 }}>{entry.body}</Typography>
        {!!(entry.tips && entry.tips.length) && (
          <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.25 }}>
            {entry.tips.map((t) => (
              <Typography key={t} component="li" sx={{ fontSize: 12, color: '#5C6B7A', mb: 0.4 }}>{t}</Typography>
            ))}
          </Box>
        )}
        <Link component="button" type="button" onClick={goToWiki}
          sx={{ display: 'inline-block', mt: 1, fontSize: 12.5, fontWeight: 700, color: '#0D5B9A' }}>
          Open in wiki &rarr;
        </Link>
      </Popover>
    </>
  );
}
